import { Card, CardBody, CardHeader, Colors, Icon, Text } from '@amelio/ui-library';
import { Trans } from 'react-i18next';

import type { LaunchChangeText } from '@/pages/CycleEvaluation/LaunchSummary/launchChanges/launchChangeText';

import { useAmelioTranslations } from '@/hooks';
import { CYCLE_SECTION_SURFACE } from '@/pages/CycleEvaluation/components/cycleCardSurface';

const CHANGES_SURFACE_BG_COLOR =
    'bg-gradient-to-br from-step-rail-violet/5 to-step-rail-turquoise/5';

export interface ChangesCardProps {
    changes: LaunchChangeText[];
}

const ChangeLine = ({ change }: { change: LaunchChangeText }) => {
    const { t } = useAmelioTranslations();

    return (
        <li
            className={'flex items-start gap-3'}
            data-testid={`launch-summary-change-${change.i18nKey}`}
        >
            <Text
                color={Colors.STEP_RAIL_VIOLET}
                className={'mt-[2px] shrink-0 text-sm leading-none'}
            >
                •
            </Text>

            <div>
                <Text className={'text-sm'}>
                    <Trans
                        i18nKey={change.i18nKey}
                        values={change.values}
                        components={{ bold: <strong className={'font-semibold'} /> }}
                    />
                </Text>

                {change.isAlreadyTriggered && (
                    <div className={'mt-[4px] flex items-center gap-1.5'}>
                        <Icon
                            name={'essential-info-circle'}
                            color={Colors.STEP_RAIL_MUTED}
                            size={14}
                        />

                        <Text
                            color={Colors.STEP_RAIL_MUTED}
                            className={'text-xs italic'}
                        >
                            {t('cycleEvaluationSchedule.launchSummary.changes.alreadyTriggered')}
                        </Text>
                    </div>
                )}
            </div>
        </li>
    );
};

export const ChangesCard = ({ changes }: ChangesCardProps) => {
    const { t } = useAmelioTranslations();

    return (
        <Card
            padding={'none'}
            className={CYCLE_SECTION_SURFACE}
            bgColor={CHANGES_SURFACE_BG_COLOR}
            borderColor={Colors.STEP_RAIL_VIOLET_30}
            dataTestId={'launch-summary-changes'}
        >
            <CardHeader
                icon={
                    <Icon
                        name={'lucide-sparkles'}
                        color={Colors.STEP_RAIL_VIOLET}
                        size={16}
                    />
                }
                title={t('cycleEvaluationSchedule.launchSummary.changes.title')}
            />

            <CardBody>
                {changes.length === 0 ? (
                    <Text
                        color={Colors.STEP_RAIL_MUTED}
                        className={'text-sm italic'}
                    >
                        {t('cycleEvaluationSchedule.launchSummary.changes.none')}
                    </Text>
                ) : (
                    <ul className={'flex flex-col gap-3'}>
                        {changes.map((change, index) => (
                            <ChangeLine
                                key={`${change.i18nKey}-${index}`}
                                change={change}
                            />
                        ))}
                    </ul>
                )}
            </CardBody>
        </Card>
    );
};
